import React, { useState } from 'react';
import ProfileInfoSection from './ProfileInfoSection';
import HistoryChart from './HistoryChart';

import { BACKEND_URL } from '../config';

function UserDetailView({ user, onBack, token, adminUserId, onSendMessage, onCreateGoal, onRefreshUsers, showToast }) {
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [showCaloriesForm, setShowCaloriesForm] = useState(false);
  const [messageSubject, setMessageSubject] = useState('');
  const [messageText, setMessageText] = useState('');
  const [goalTitle, setGoalTitle] = useState('');
  const [goalDescription, setGoalDescription] = useState('');
  const [goalDueDate, setGoalDueDate] = useState('');
  const [goalTargetValue, setGoalTargetValue] = useState('');
  const [goalTargetUnit, setGoalTargetUnit] = useState('calories');
  const [newDailyTarget, setNewDailyTarget] = useState(user.profile?.daily_target || '');
  const [sending, setSending] = useState(false);
  const [userStatus, setUserStatus] = useState({
    approved: user.auth_info.approved !== false,
    is_admin: user.auth_info.is_admin || false
  });
  const [currentDailyTarget, setCurrentDailyTarget] = useState(user.profile?.daily_target);

  const handleApproveUser = async () => {
    setSending(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/approve/${user.auth_info.user_id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        setUserStatus(prev => ({ ...prev, approved: true }));
        showToast?.('User approved successfully! They will receive an email notification.');
        if (onRefreshUsers) onRefreshUsers();
      } else {
        const error = await response.json();
        showToast?.('Error: ' + error.detail, "error");
      }
    } catch (error) {
      showToast?.('Error approving user: ' + error.message, "error");
    }
    setSending(false);
  };

  const handleMakeAdmin = async () => {
    if (!window.confirm(`Are you sure you want to make ${user.auth_info.name} an admin?`)) return;
    
    setSending(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/make-admin/${user.auth_info.user_id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        setUserStatus({ approved: true, is_admin: true });
        showToast?.('User promoted to admin! They will receive an email notification.');
        if (onRefreshUsers) onRefreshUsers();
      } else {
        const error = await response.json();
        showToast?.('Error: ' + error.detail, "error");
      }
    } catch (error) {
      showToast?.('Error promoting user: ' + error.message, "error");
    }
    setSending(false);
  };

  const handleRevokeAdmin = async () => {
    if (!window.confirm(`Are you sure you want to revoke admin status from ${user.auth_info.name}?`)) return;
    
    setSending(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/revoke-admin/${user.auth_info.user_id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        setUserStatus(prev => ({ ...prev, is_admin: false }));
        showToast?.('Admin status revoked.');
        if (onRefreshUsers) onRefreshUsers();
      } else {
        const error = await response.json();
        showToast?.('Error: ' + error.detail, "error");
      }
    } catch (error) {
      showToast?.('Error revoking admin: ' + error.message, "error");
    }
    setSending(false);
  };

  const handleDeleteUser = async () => {
    if (!window.confirm(`Are you sure you want to DELETE ${user.auth_info.name}? This will permanently remove all their data and cannot be undone.`)) return;
    if (!window.confirm(`FINAL WARNING: This action is IRREVERSIBLE. Type "delete" to confirm.`)) return;
    
    setSending(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/user/${user.auth_info.user_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        showToast?.('User deleted successfully.');
        if (onRefreshUsers) onRefreshUsers();
        onBack();
      } else {
        const error = await response.json();
        showToast?.('Error: ' + error.detail, "error");
      }
    } catch (error) {
      showToast?.('Error deleting user: ' + error.message, "error");
    }
    setSending(false);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    setSending(true);
    
    try {
      await onSendMessage({
        from_user_id: adminUserId,
        to_user_id: user.auth_info.user_id,
        subject: messageSubject,
        message: messageText
      });
      
      setShowMessageForm(false);
      setMessageSubject('');
      setMessageText('');
      showToast?.('Message sent successfully!');
    } catch (error) {
      showToast?.('Error sending message: ' + error.message, "error");
    }
    
    setSending(false);
  };

  const handleCreateGoal = async (e) => {
    e.preventDefault();
    setSending(true);
    
    try {
      await onCreateGoal({
        user_id: user.auth_info.user_id,
        title: goalTitle,
        description: goalDescription,
        target_value: goalTargetValue ? parseFloat(goalTargetValue) : null,
        target_unit: goalTargetUnit,
        due_date: goalDueDate || null,
        assigned_by: adminUserId
      });
      
      setShowGoalForm(false);
      setGoalTitle('');
      setGoalDescription('');
      setGoalDueDate('');
      setGoalTargetValue('');
      setGoalTargetUnit('calories');
      showToast?.('Goal assigned successfully!');
    } catch (error) {
      showToast?.('Error creating goal: ' + error.message, "error");
    }
    
    setSending(false);
  };

  const handleUpdateCalories = async (e) => {
    e.preventDefault();
    if (!newDailyTarget || isNaN(parseFloat(newDailyTarget))) {
      showToast?.('Please enter a valid calorie target', "error");
      return;
    }

    setSending(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/user/${user.auth_info.user_id}/calories?daily_target=${parseFloat(newDailyTarget)}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCurrentDailyTarget(parseFloat(newDailyTarget));
        setShowCaloriesForm(false);
        showToast?.(`Daily calorie target updated to ${newDailyTarget} calories!`);
        if (onRefreshUsers) onRefreshUsers();
      } else {
        const error = await response.json();
        showToast?.('Error: ' + error.detail, "error");
      }
    } catch (error) {
      showToast?.('Error updating calories: ' + error.message, "error");
    }
    setSending(false);
  };

  return (
    <div className="admin-view">
      <button onClick={onBack} className="btn-secondary" style={{ marginBottom: '24px' }}>
        ← Back to Users
      </button>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
        <h1 className="heading-2" style={{ margin: 0 }}>{user.auth_info.name}</h1>
        {userStatus.is_admin && <span className="admin-badge">Admin</span>}
        {!userStatus.approved && (
          <span style={{ 
            background: '#fef3c7', 
            color: '#92400e', 
            padding: '4px 12px', 
            borderRadius: '100px',
            fontSize: '12px',
            fontWeight: 600
          }}>Pending Approval</span>
        )}
      </div>
      <p className="body-medium" style={{ marginBottom: '24px', color: 'var(--text-secondary)' }}>
        {user.auth_info.email}
      </p>
      
      {/* Admin Actions */}
      <div style={{ 
        background: 'var(--bg-card)', 
        border: '1px solid var(--border-light)', 
        borderRadius: '12px', 
        padding: '20px', 
        marginBottom: '24px' 
      }}>
        <h3 className="heading-4" style={{ marginBottom: '16px' }}>Admin Actions</h3>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {!userStatus.approved && (
            <button 
              onClick={handleApproveUser}
              disabled={sending}
              className="btn-primary"
              style={{ background: 'linear-gradient(168deg, #16a34a -63%, #15803d 100%)' }}
              data-testid="approve-user-btn"
            >
              {sending ? 'Approving...' : '✓ Approve User'}
            </button>
          )}
          
          {!userStatus.is_admin ? (
            <button 
              onClick={handleMakeAdmin}
              disabled={sending}
              className="btn-secondary"
              data-testid="make-admin-btn"
            >
              {sending ? 'Processing...' : '👑 Make Admin'}
            </button>
          ) : (
            <button 
              onClick={handleRevokeAdmin}
              disabled={sending}
              className="btn-secondary"
              style={{ color: '#dc2626', borderColor: '#dc2626' }}
              data-testid="revoke-admin-btn"
            >
              {sending ? 'Processing...' : 'Revoke Admin'}
            </button>
          )}
          
          <button 
            onClick={() => { setShowMessageForm(!showMessageForm); setShowGoalForm(false); }} 
            className="btn-secondary"
            data-testid="send-message-btn"
          >
            {showMessageForm ? 'Cancel' : '✉ Send Message'}
          </button>
          
          <button 
            onClick={() => { setShowGoalForm(!showGoalForm); setShowMessageForm(false); setShowCaloriesForm(false); }} 
            className="btn-secondary"
            data-testid="assign-goal-btn"
          >
            {showGoalForm ? 'Cancel' : '🎯 Assign Goal'}
          </button>

          <button 
            onClick={() => { setShowCaloriesForm(!showCaloriesForm); setShowMessageForm(false); setShowGoalForm(false); }} 
            className="btn-secondary"
            data-testid="set-calories-btn"
          >
            {showCaloriesForm ? 'Cancel' : '🔥 Set Calories'}
          </button>
          
          <button 
            onClick={handleDeleteUser}
            disabled={sending}
            className="btn-secondary"
            style={{ color: '#dc2626', borderColor: '#dc2626', marginLeft: 'auto' }}
            data-testid="delete-user-btn"
          >
            {sending ? 'Deleting...' : '🗑 Delete User'}
          </button>
        </div>
      </div>

      {/* Message Form */}
      {showMessageForm && (
        <div className="form-section" style={{ marginBottom: '32px' }}>
          <h3 className="heading-3">Send Message to {user.auth_info.name}</h3>
          <form onSubmit={handleSendMessage}>
            <div className="form-group">
              <label className="body-medium">Subject</label>
              <input 
                type="text" 
                value={messageSubject}
                onChange={(e) => setMessageSubject(e.target.value)}
                className="form-input"
                placeholder="Message subject"
                data-testid="admin-message-subject"
              />
            </div>
            <div className="form-group">
              <label className="body-medium">Message</label>
              <textarea 
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                required 
                className="form-input"
                rows="5"
                placeholder="Type your message..."
                data-testid="admin-message-text"
              />
            </div>
            <button type="submit" className="btn-primary" disabled={sending} data-testid="admin-send-message-submit">
              {sending ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      )}

      {/* Goal Form */}
      {showGoalForm && (
        <div className="form-section" style={{ marginBottom: '32px' }}>
          <h3 className="heading-3">Assign Goal to {user.auth_info.name}</h3>
          <form onSubmit={handleCreateGoal}>
            <div className="form-group">
              <label className="body-medium">Goal Title</label>
              <input 
                type="text" 
                value={goalTitle}
                onChange={(e) => setGoalTitle(e.target.value)}
                required
                className="form-input"
                placeholder="e.g., Lose 5 pounds"
                data-testid="admin-goal-title"
              />
            </div>
            <div className="form-group">
              <label className="body-medium">Description</label>
              <textarea 
                value={goalDescription}
                onChange={(e) => setGoalDescription(e.target.value)}
                className="form-input"
                rows="3"
                placeholder="Detailed description of the goal..."
                data-testid="admin-goal-description"
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="body-medium">Target Value</label>
                <input 
                  type="number" 
                  value={goalTargetValue}
                  onChange={(e) => setGoalTargetValue(e.target.value)}
                  className="form-input"
                  placeholder="e.g., 2000"
                  data-testid="admin-goal-target-value"
                />
              </div>
              <div className="form-group">
                <label className="body-medium">Unit</label>
                <select 
                  value={goalTargetUnit}
                  onChange={(e) => setGoalTargetUnit(e.target.value)}
                  className="form-input"
                  data-testid="admin-goal-target-unit"
                >
                  <option value="calories">Calories</option>
                  <option value="lbs">Pounds</option>
                  <option value="minutes">Minutes</option>
                  <option value="days">Days</option>
                  <option value="meals">Meals</option>
                  <option value="workouts">Workouts</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="body-medium">Due Date</label>
              <input 
                type="date" 
                value={goalDueDate}
                onChange={(e) => setGoalDueDate(e.target.value)}
                className="form-input"
                data-testid="admin-goal-due-date"
              />
            </div>
            <button type="submit" className="btn-primary" disabled={sending} data-testid="admin-assign-goal-submit">
              {sending ? 'Assigning...' : 'Assign Goal'}
            </button>
          </form>
        </div>
      )}

      {/* Calories Form */}
      {showCaloriesForm && (
        <div className="form-section" style={{ marginBottom: '32px' }}>
          <h3 className="heading-3">Set Daily Calorie Target for {user.auth_info.name}</h3>
          <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
            Current target: <strong>{currentDailyTarget ? `${currentDailyTarget.toFixed(0)} calories` : 'Not set'}</strong>
          </p>
          <form onSubmit={handleUpdateCalories}>
            <div className="form-group">
              <label className="body-medium">New Daily Calorie Target</label>
              <input 
                type="number" 
                value={newDailyTarget}
                onChange={(e) => setNewDailyTarget(e.target.value)}
                required
                min="1000"
                max="10000"
                className="form-input"
                placeholder="e.g., 2000"
                data-testid="admin-calories-input"
              />
              <p className="body-small" style={{ color: 'var(--text-muted)', marginTop: '8px' }}>
                Recommended range: 1,200 - 4,000 calories depending on goals
              </p>
            </div>
            <button type="submit" className="btn-primary" disabled={sending} data-testid="admin-update-calories-submit">
              {sending ? 'Updating...' : 'Update Calorie Target'}
            </button>
          </form>
        </div>
      )}
      
      <ProfileInfoSection profile={user.profile} />
      
      {user.profile && <HistoryChart history={user.history} />}
    </div>
  );
}

export default UserDetailView;
